import React, { useState, useEffect } from 'react'
import { useSpring, animated } from 'react-spring'
import Button from '../button'
import { ButtonProps as SemanticButtonProps } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

// TODO: refactor Button so this comes from the button component
export interface CoreButtonProps
  extends Pick<
    SemanticButtonProps,
    | 'active'
    | 'disabled'
    | 'floated'
    | 'fluid'
    | 'icon'
    | 'loading'
    | 'onClick'
    | 'tabIndex'
    | 'type'
  > {
  children?: React.ReactNode
  role: 'primary' | 'secondary'
  color?: 'info' | 'success' | 'caution' | 'error' | 'basic'
  size?: 'small' | 'medium' | 'large' | 'marshmallow'
  tag?: 'button' | 'div' | 'a'
  isStretch?: boolean
  isSelected?: boolean
  link?: string
  className?: string
  elmId?: string
}

interface CountdownButtonProps extends CoreButtonProps {
  seconds?: number
  label?: string
  confirmRole?: CoreButtonProps['role']
  confirmColor?: CoreButtonProps['color']
  confirmLabel?: string
  style?: React.CSSProperties | undefined
  reInitialize?: boolean
}

type OnClickArgs = {
  event: React.MouseEvent<HTMLButtonElement>
  data: SemanticButtonProps
}

// const y: CountdownButtonProps = {}

const CountdownButton: React.FC<CountdownButtonProps> = ({
  seconds = 5,
  confirmRole,
  confirmColor,
  confirmLabel = 'Click to Confirm:',
  onClick,
  role: _role,
  color: _color,
  children,
  disabled,
  style = {},
  reInitialize = false,
  ...rest
}) => {
  const [count, setCount] = useState(-1)
  const [role, setRole] = useState<CountdownButtonProps['role']>(_role)
  const [color, setColor] = useState<CountdownButtonProps['color']>(_color)
  const [done, setDone] = useState(false)
  const [onClickArgs, setOnClickArgs] = useState<OnClickArgs>()

  const barStyles = useSpring({
    from: { width: count > 0 ? '100%' : '0%' },
    to: { width: '0%' },
    config: { duration: seconds * 1000 }, // Duration in milliseconds
    reset: count === seconds,
  })

  useEffect(() => {
    setRole(_role)
  }, [_role])

  useEffect(() => {
    setColor(_color)
  }, [_color])

  useEffect(() => {
    if (count === 0 || done) {
      setRole(_role)
      setColor(_color)
    }
  }, [_role, _color, count, done])

  useEffect(() => {
    if (reInitialize && done) {
      setDone(false)
      setCount(-1)
    }
  }, [reInitialize, done])

  const onClickHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    data: SemanticButtonProps
  ) => {
    if (count > 0) {
      // user confirmed during countdown!
      if (onClickArgs && onClick) {
        onClick(onClickArgs?.event, onClickArgs?.data)
      }
      setRole(_role)
      setDone(true)
    } else {
      // start the countdown
      setRole(confirmRole || _role)
      setColor(confirmColor || _color)
      setOnClickArgs({ event, data })
      setCount(seconds)
    }
  }

  useEffect(() => {
    // Stop the countdown when it reaches 0 or when the user confirms
    if (count === 0 || done) {
      return
    }
    const timerId = setTimeout(() => setCount(count - 1), 1000)

    return () => clearTimeout(timerId)
  }, [count, done, onClick, onClickArgs])

  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        ...style,
      }}
    >
      <Button
        onClick={onClickHandler}
        role={role}
        color={color}
        disabled={disabled || done}
        {...rest}
      >
        {count > 0 ? `${confirmLabel} ${count}` : children}
      </Button>
      {count > 0 && (
        <animated.div
          className={cx('bar', role === 'primary' ? 'white' : color)}
          style={{
            ...barStyles,
            height: '3px',
            marginTop: '-3px',
          }}
        />
      )}
    </div>
  )
}

export default CountdownButton
